import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CAR_LIST_SEARCH,
  RESERVE_DETAIL_SEARCH,
  RESERVE_INFO_SEARCH,
  RESERVE_DETAIL_SEARCH_OPEN_TIME,
  SERVICE_LIST_SEARCH,
  SHOP_LIST_SEARCH,
  SHOP_STATUS_SEARCH,
  USER_TIRE_STORAGE,
  USER_WAIT_TIME,
  USER_WAIT_TIME_RESERVE,
  SHOP_SERVICE_SEARCH_EXTERNAL,
} from 'constants/apiURL';
import { getService } from 'pages/reservation/utils/reservationUtil';
import axios from '../utils/axiosUtil';
import { loadingActions } from './loadingSlice';

const initialState = {
  selectedSrySeq: null,
  carList: [],
  shopInfo: {},
  shopList: [],
  dailyInfo: [],
  menuComment: '',
  requiredTime: '',
  nextWeekFlag: '0',
  targetIndex: -1,
  outOfTermIndex: -1,
  outOfTermMessage: '',
  waitTimeInfo: null,
  selectService: null,
  externalSelectSerivce: null,
  shopService: null,
  shopStatus: null,
  showDescriptionModal: false,
  showCalendarModal: false,
  showVehicleRegisterModal: false,
  showRankModal: false,
  showMailAddressSettingModal: false,
  showApproximateTimeUpdateModal: false,
  shopAvailabilityLoading: false,
  isTireContract: false,
  // EC購入導線の日時変更（ecYykNo）で取得した実予約データ。該当予約が無い場合はnull
  ecModifyYykInfo: null,
  // ecModifyYykInfoがどのecYykNoに対して取得済みかを保持する（多重発火防止・再取得判定用）
  ecModifyYykInfoFetchedYykNo: null,
  // 予約情報取得APIが失敗（rejected）したか。エラーモーダルはaxiosインターセプタが表示済みのため、
  // ラッパー側ではEC動線コンテキストのクリアとTOPへの遷移のみ行う
  ecModifyYykInfoFetchFailed: false,
};

export const fetchShopServiceExternal = createAsyncThunk(
  'fetchShopServiceExternal',
  async params => {
    const serviceRes = await axios.get(SHOP_SERVICE_SEARCH_EXTERNAL, {
      params: { shopCode: params },
    });

    return serviceRes.body;
  }
);

export const fetchDateSelectInit = createAsyncThunk(
  'fetchDateSelectInit',
  async (params, { dispatch }) => {
    dispatch(loadingActions.setPrimaryLoading(true));

    const [carRes, shopRes, statusRes, shopServiceRes, tireRes] =
      await Promise.all([
        axios.get(CAR_LIST_SEARCH),
        axios.get(SHOP_LIST_SEARCH, { params: { kbn: '5' } }),
        axios.get(SHOP_STATUS_SEARCH.replace('{shopCode}', params.shopCode)),
        axios.get(SERVICE_LIST_SEARCH, {
          params: { tokCd: params.shopCode, isMain: '0', sortNum: 0 },
        }),
        axios.post(USER_TIRE_STORAGE, { userId: params.userId }),
      ]);

    let userWaitTimeRes;

    try {
      userWaitTimeRes = await axios.post(
        USER_WAIT_TIME,
        {
          shopCode: params.shopCode,
          chubunCd: params.chubunCd,
          shobunCd: params.shobunCd[0]?.shobunCd,
        },
        { ignoreError: true }
      );
    } catch (error) {}

    const shopList = shopRes.body.shopList.map(shop => ({
      ...shop,
      value: shop.shopCode,
      label: shop.shopName,
    }));

    const selectService = getService(
      shopServiceRes?.body || [],
      params.shobunCd
    );

    dispatch(loadingActions.setPrimaryLoading(false));

    const { getTime, menus, frontStatus } = userWaitTimeRes?.body ?? {};

    const { menuCd, confirmMenuWaitMinutes, shobun = [] } = menus?.[0] ?? {};

    return {
      carList: carRes.body.carList.map(car => ({
        ...car,
        value: car.sryMkrCd,
        label: car.sryMkrNm1,
      })),
      shopList,
      shopInfo: shopList.find(s => s.shopCode === params.shopCode),
      waitTimeInfo: {
        confirmMenuWaitMinutes,
        getTime,
        menuCd,
        chubunCd: params.chubunCd,
        shobunCd: shobun[0]?.shobunCd,
        sgyTime: shobun[0]?.sgyTime,
        frontStatus,
      },
      shopStatus: statusRes.body.shopStatus,
      selectService,
      shopService: shopServiceRes?.body || [],
      isTireContract: tireRes?.body?.contractStatus === '1',
    };
  }
);

export const fetchShopAvailability = createAsyncThunk(
  'fetchShopAvailability',
  async (params, { dispatch }) => {
    dispatch(actions.setShopAvailabilityLoading(true));
    const { useOpenTimeSearch, ...requestParams } = params;

    const requestPath = useOpenTimeSearch
      ? RESERVE_DETAIL_SEARCH_OPEN_TIME
      : RESERVE_DETAIL_SEARCH;

    try {
      const [reserveDetailRes] = await Promise.all([
        axios.get(requestPath, {
          params: requestParams,
          hiddenLoading: true,
        }),
      ]);
      return reserveDetailRes?.body;
    } finally {
      dispatch(actions.setShopAvailabilityLoading(false));
    }
  }
);

export const fetchUserWaitTime = createAsyncThunk(
  'dateSelect/fetchUserWaitTime',
  async params => axios.post(USER_WAIT_TIME, params)
);

export const updateUserWaitTime = createAsyncThunk(
  'dateSelect/updateUserWaitTime',
  async params => {
    const userWaitTimeRes = await axios.post(USER_WAIT_TIME, params, {
      ignoreError: true,
    });

    const { getTime, menus, frontStatus } = userWaitTimeRes?.body ?? {};

    const { menuCd, confirmMenuWaitMinutes, shobun = [] } = menus?.[0] ?? {};

    return {
      waitTimeInfo: {
        confirmMenuWaitMinutes,
        getTime,
        menuCd,
        chubunCd: params.chubunCd,
        shobunCd: shobun[0]?.shobunCd,
        sgyTime: shobun[0]?.sgyTime,
        frontStatus,
      },
    };
  }
);

export const waitTimeReserve = createAsyncThunk(
  'dateSelect/waitTimeReserve',
  async params => axios.post(USER_WAIT_TIME_RESERVE, params)
);

// EC購入導線の日時変更で、対象予約（ecYykNo）の実予約データを予約情報取得APIから取得する
// （予約一覧画面 reservationListSlice.fetchReserveList と同じAPI・パラメータを使用）
export const fetchEcModifyYykInfo = createAsyncThunk(
  'dateSelect/fetchEcModifyYykInfo',
  async params => {
    const reserveRes = await axios.post(RESERVE_INFO_SEARCH, {
      kaiinNo: params.kaiinNo,
      dateKbn: '1',
    });

    // yykGetYmd/yykGetHmsはレスポンス直下にあるため、予約一覧と同様に該当予約へマージする
    // （reservationListSlice.convertReserveInfoと同じ形。確認画面の予約変更確定APIで使用される）
    const {
      yykGetYmd,
      yykGetHms,
      reservationInfo = [],
    } = reserveRes?.body ?? {};
    const matched = reservationInfo.find(r => r.yykNo === params.ecYykNo);

    return {
      ecModifyYykInfo: matched ? { ...matched, yykGetYmd, yykGetHms } : null,
      ecModifyYykInfoFetchedYykNo: params.ecYykNo,
    };
  }
);

export const reservationDateSelectSlice = createSlice({
  name: 'reservationDateSelect',
  initialState,
  reducers: {
    setSrySeq: (state, { payload }) => {
      state.selectedSrySeq = payload;
    },
    showDescriptionModal: state => {
      state.showDescriptionModal = true;
    },
    closeDescriptionModal: state => {
      state.showDescriptionModal = false;
    },
    toggleDescriptionModal: state => {
      state.showDescriptionModal = !state.showDescriptionModal;
    },
    showCalendarModal: state => {
      state.showCalendarModal = true;
    },
    closeCalendarModal: state => {
      state.showCalendarModal = false;
    },
    showVehicleRegisterModal: state => {
      state.showVehicleRegisterModal = true;
    },
    closeVehicleRegisterModal: state => {
      state.showVehicleRegisterModal = false;
    },
    updateWaitTime: (state, { payload }) => {
      state.waitTimeInfo = payload;
    },
    updateExternalSelectService: (state, { payload }) => {
      state.externalSelectSerivce = payload;
    },
    showRankModal: state => {
      state.showRankModal = true;
    },
    closeRankModal: state => {
      state.showRankModal = false;
    },
    showMailAddressSettingModal: state => {
      state.showMailAddressSettingModal = true;
    },
    showApproximateTimeUpdateModal: state => {
      state.showApproximateTimeUpdateModal = true;
    },
    closeModal: state => {
      state.showMailAddressSettingModal = false;
      state.showApproximateTimeUpdateModal = false;
    },
    setShopAvailabilityLoading: (state, { payload }) => {
      state.shopAvailabilityLoading = payload;
    },
    clear: () => initialState,
  },
  extraReducers: builder => {
    builder.addCase(fetchDateSelectInit.fulfilled, (state, { payload }) => ({
      ...state,
      ...payload,
    }));
    builder.addCase(fetchShopAvailability.fulfilled, (state, { payload }) => ({
      ...state,
      ...payload,
    }));
    builder.addCase(updateUserWaitTime.fulfilled, (state, { payload }) => ({
      ...state,
      ...payload,
    }));
    builder.addCase(fetchEcModifyYykInfo.fulfilled, (state, { payload }) => ({
      ...state,
      ...payload,
      ecModifyYykInfoFetchFailed: false,
    }));
    builder.addCase(fetchEcModifyYykInfo.rejected, state => ({
      ...state,
      ecModifyYykInfoFetchFailed: true,
    }));
  },
});

export const actions = reservationDateSelectSlice.actions;
export default reservationDateSelectSlice.reducer;
